import { useState, useEffect } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  DialogContentText,
  TextField,
  Autocomplete,
  CircularProgress,
  Box,
  Typography,
  Chip,
} from "@mui/material";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";

const DEFAULT_FORM_STATE = {
  partnership: null,
};

/**
 * @typedef {object} TargetAdminDetails
 * @property {string} name
 * @property {string} email
 */

/**
 * @typedef {object} PartnershipData
 * @property {number} id
 * @property {number} base_cost
 * @property {boolean} is_active
 * @property {TargetAdminDetails} target_admin_details
 */

/**
 * @typedef {object} AssignmentFormData
 * @property {number} partnership_id - The ID of the selected alliance
 */

/**
 * A modal for mass assigning deliveries to a partner
 *
 * @param {object} props
 * @param {boolean} props.open - Whether the modal is open
 * @param {function} props.onClose - Function to call when closing the modal
 * @param {function(AssignmentFormData): Promise<void>} props.onConfirm - Async function to call with form data
 * @param {boolean} props.loading - Whether the form is currently submitting
 * @param {Array<PartnershipData>} [props.activePartnerships=[]] - List of active alliances
 * @param {number} props.selectedCount - Number of deliveries selected
 */

const AssignDeliveriesModal = ({
  open,
  onClose,
  onConfirm,
  loading = false,
  activePartnerships = [],
  selectedCount = 0,
}) => {
  const [formData, setFormData] = useState(DEFAULT_FORM_STATE);
  const [formErrors, setFormErrors] = useState({});

  const isFormValid = formData.partnership !== null;

  useEffect(() => {
    if (open) {
      setFormData(DEFAULT_FORM_STATE);
      setFormErrors({});
    }
  }, [open]);

  const handlePartnershipChange = (event, newValue) => {
    setFormData((previous) => ({ ...previous, partnership: newValue }));
    if (formErrors.partnership) {
      setFormErrors((previous) => ({ ...previous, partnership: null }));
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.partnership) {
      errors.partnership =
        "Debes seleccionar una alianza para derivar los envíos";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    onConfirm(formData.partnership.id);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Derivar Envíos a Partner</DialogTitle>

      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          Estás a punto de derivar <strong>{selectedCount} envíos</strong>.
          Selecciona la alianza comercial que se encargará del despacho.
        </DialogContentText>

        <Autocomplete
          id="partnership"
          options={activePartnerships}
          getOptionLabel={(option) =>
            option.target_admin_details?.name || "Sin Nombre"
          }
          value={formData.partnership}
          onChange={handlePartnershipChange}
          loading={loading}
          noOptionsText="No tienes alianzas activas"
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  width: "100%",
                  alignItems: "center",
                }}
              >
                <span>{option.target_admin_details?.name}</span>
              </Box>
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              margin="dense"
              label="Seleccionar Partner"
              variant="outlined"
              error={!!formErrors.partnership}
              helperText={formErrors.partnership}
              required
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />

        {formErrors.submit && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {formErrors.submit}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !isFormValid}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            "Confirmar Derivación"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssignDeliveriesModal;